import {HttpHeaders} from '@angular/common/http';
import {AppSetting} from '../AppSetting';
import {AppSettingApi} from '../AppSettingApi';
import {BaseService} from '../Service/BaseService';
import {BaseWebApiDAO} from './BaseWebApiDAO';
import {HtmlMailImageDTOInterface} from '../DTO/HtmlMailImageDTOInterface';
import {toCamel} from '../Util/SnakeCamelUtil';

/**
 * HtmlMailImageDAO
 * HTMLメール画像DAO
 */
export class HtmlMailImageDAO extends BaseWebApiDAO {

	constructor(svc: BaseService) {
		super(svc);
	}

	/**
	 * 画像一覧取得
	 * @param status 取得する状態（HtmlMailImageStatusConst）。
	 *               指定しない場合、APIは一覧非表示の画像も含めた全件を返す。
	 */
	public async getList(status?: string): Promise<Array<HtmlMailImageDTOInterface>> {

		if (AppSetting.DEBUG_MODE_DUMYDATA) {
			return [] as Array<HtmlMailImageDTOInterface>;
		}

		const request: {t: string; status?: string} = {
			t: this.token
		};

		// 状態の指定がある時だけ送る。キーごと送らないことで全件取得になる。
		if (this.isStatusSpecified(status)) {
			request.status = status;
		}

		const res = await this.postRequest(AppSettingApi.HTML_MAIL_IMAGE_LIST, request);
		const list = this.obj2Array(toCamel(res)) as Array<HtmlMailImageDTOInterface>;
		list.forEach(image => this.normalizeImage(image));
		return list;
	}

	/**
	 * 状態の指定があるかどうか。
	 * 未指定・空文字はAPIでは「状態を問わず全件」を意味するため、リクエストに含めない。
	 */
	private isStatusSpecified(status: string): boolean {
		if (status === undefined) {
			return false;
		}
		if (status === null) {
			return false;
		}
		if (status === '') {
			return false;
		}
		return true;
	}

	/**
	 * 一覧の画像を画面が扱いやすい形へ揃える。
	 * revision は設定更新・LINE添付の必須項目のため、未設定の画像は初回設定を表す 0 にする
	 * （undefined のままだとリクエストのJSONからキーごと落ちてしまう）。
	 * LINE対応済みの画像は、実際にLINEへ配信される派生画像をプレビューに使う。
	 */
	private normalizeImage(image: HtmlMailImageDTOInterface): void {
		if (image.revision === undefined || image.revision === null) {
			image.revision = 0;
		}
		if (image.previewUri) {
			image.uri = image.previewUri;
		}
	}

	/**
	 * 画像アップロード
	 * @param file アップロードするファイル
	 */
	public async upload(file: File, linkUrl: string, altText: string): Promise<HtmlMailImageDTOInterface> {

		if (AppSetting.DEBUG_MODE_DUMYDATA) {
			return {} as HtmlMailImageDTOInterface;
		}

		const formData = new FormData();
		formData.append('t', this.token);
		formData.append('upload_file', file);
		formData.append('link_url', linkUrl);
		formData.append('alt_text', altText);

		// multipart/form-data のため Content-Type ヘッダーを設定しない（ブラウザが自動設定）
		const res = await this.postRequest(AppSettingApi.HTML_MAIL_IMAGE_UPLOAD, formData, {headers: new HttpHeaders()});
		return toCamel(res) as HtmlMailImageDTOInterface;
	}

	/** URL・ALTを保存する。revision=0は既存画像の初回設定。 */
	public async update(id: number, revision: number, linkUrl: string, altText: string): Promise<HtmlMailImageDTOInterface> {
		const request = {t: this.token, id, revision, link_url: linkUrl, alt_text: altText};
		const res = await this.postRequest(AppSettingApi.HTML_MAIL_IMAGE_UPDATE, request);
		if (!res) {
			return null;
		}
		return toCamel(res) as HtmlMailImageDTOInterface;
	}

	/**
	 * 画像削除
	 * @param id ファイルID
	 */
	public async delete(id: number): Promise<any> {

		if (AppSetting.DEBUG_MODE_DUMYDATA) {
			return '';
		}

		const request = {
			t: this.token,
			id
		};

		const res = await this.postRequest(AppSettingApi.HTML_MAIL_IMAGE_DELETE, request);
		return res;
	}

	/**
	 * 一覧非表示にする
	 * 画像・設定・元画像IDは残るため、送信済み・受付済みのメッセージには影響しない。
	 * @param id ファイルID
	 */
	public async disable(id: number): Promise<any> {

		if (AppSetting.DEBUG_MODE_DUMYDATA) {
			return '';
		}

		const request = {
			t: this.token,
			id
		};

		const res = await this.postRequest(AppSettingApi.HTML_MAIL_IMAGE_DISABLE, request);
		return res;
	}

	/**
	 * 一覧非表示を解除して再表示する
	 * @param id ファイルID
	 */
	public async enable(id: number): Promise<any> {

		if (AppSetting.DEBUG_MODE_DUMYDATA) {
			return '';
		}

		const request = {
			t: this.token,
			id
		};

		const res = await this.postRequest(AppSettingApi.HTML_MAIL_IMAGE_ENABLE, request);
		return res;
	}
}
